// Parse an iReal Pro `irealb://` / `irealbook://` URL into one or more charts.
//
// The header is a list of `=`-separated fields; the music field is unscrambled
// (for `irealb://`) and tokenized into measures. Songs in a playlist are
// separated by `===`, with the playlist name trailing.

import { uid } from '../song';
import { unscramble, MUSIC_PREFIX } from './unscramble';
import { parseChordToken, prettyChordSymbol } from './chordParser';
import type {
  BarlineClose,
  BarlineOpen,
  IRealChart,
  IRealChord,
  IRealChordRef,
  IRealMeasure,
  IRealPlaylist,
} from './types';

const SCHEME_RE = /(irealb(?:ook)?):\/\/(.*)$/s;

/** Does this text look like an iReal Pro link? */
export function looksLikeIReal(text: string): boolean {
  return /irealb(?:ook)?:\/\//.test(text);
}

function decode(payload: string): string {
  try {
    return decodeURIComponent(payload);
  } catch {
    // Some exports double-encode or contain stray %; fall back to a lenient pass.
    return payload.replace(/%([0-9A-Fa-f]{2})/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
  }
}

function normalizeTitle(title: string): string {
  return title.replace(/^(.*)(, )(A|The)$/, '$3 $1').trim();
}

function normalizeComposer(composer: string): string {
  const parts = composer.trim().split(/\s+/);
  // iReal stores "Last First"; swap a simple two-name pair to "First Last".
  if (parts.length === 2) return `${parts[1]} ${parts[0]}`;
  return composer.trim();
}

function normalizeKey(key: string): string {
  const k = key.trim();
  if (!k) return 'C';
  if (k.endsWith('-')) return `${k.slice(0, -1)} minor`;
  return k;
}

function parseTimeSig(a: string, b: string): [number, number] {
  if (`${a}${b}` === '12') return [12, 8];
  return [parseInt(a, 10), parseInt(b, 10)];
}

const DIRECTIVE_RE = /\bD\.\s*[CS]\.|Fine|To Coda/i;

/** Is this text a navigation directive the flattener acts on (D.C./D.S./Fine/To Coda)? */
export function isNavigationDirective(text: string): boolean {
  return DIRECTIVE_RE.test(text);
}

/** Even-as-possible integer beat split that sums to `total` (each ≥ 1). */
function distributeBeats(n: number, total: number): number[] {
  if (n <= 0) return [];
  const base = Math.floor(total / n);
  const rem = total - base * n;
  return Array.from({ length: n }, (_, k) => Math.max(1, base + (k < rem ? 1 : 0)));
}

interface PendingMeasure extends Omit<IRealMeasure, 'id'> {
  _open?: BarlineOpen;
  /** Absolute layout cell of each chord (parallel to `chords`). */
  _cells?: number[];
}

/** Tokenize the unscrambled music string into measures, preserving the cell grid. */
export function tokenizeMeasures(music: string, initialTimeSig: [number, number]): IRealMeasure[] {
  const measures: IRealMeasure[] = [];
  let running: [number, number] = initialTimeSig;
  let smallOn = false;

  // iReal Pro lays cells out 16 per row; spaces/spacers are real cells, so we
  // track an absolute cell cursor to recover line breaks and ending alignment.
  let cellCursor = 0;
  let measureStartCell = 0;
  // The most recent chord reference, so an invisible-root `W` can inherit it.
  let lastRef: IRealChordRef | null = null;
  // Whether a written N.C. is what sounds now (what a slash holds).
  let noChordInForce = false;
  // What was in force entering the first ending: a later ending follows the
  // same bar in performance, not the first ending's last chord.
  let beforeEndings: { ref: IRealChordRef | null; noChord: boolean } | null = null;

  /** A cell that sounds `ref` again without a symbol of its own (`W`, `p`). */
  const heldChord = (ref: IRealChordRef): IRealChord => ({
    id: uid('c'),
    ...ref,
    alternate: null,
    beats: 0,
    small: smallOn || undefined,
    symbol: prettyChordSymbol(ref),
  });
  const noChord = (): IRealChord => ({
    id: uid('c'),
    root: '',
    quality: '',
    chordType: null,
    beats: 0,
    noChord: true,
    symbol: 'N.C.',
  });

  const blank = (): PendingMeasure => ({ chords: [] });
  let cur = blank();

  const isReal = (m: PendingMeasure) =>
    m.chords.length > 0 ||
    m.barRepeat != null ||
    m.section != null ||
    m.ending != null ||
    m.coda ||
    m.segno ||
    m.fermata ||
    m.staffText != null ||
    m.directive != null;

  // A two-bar simile (`r`) is written as its sign bar plus an empty second
  // bar (`   r|    }`). That second bar is part of the simile, so its closing
  // barline and its text belong to the `r` measure, which resolveBarRepeats
  // hands to the second half of the expansion. Set while that bar is open.
  let simile: IRealMeasure | null = null;
  // Cells after a closing `]`, `}` or `Z` lie outside every bar until the
  // next barline: layout padding (`}        |N2`, the gap before a detached
  // coda), not a bar. Marks written there — a coda sign or ending number for
  // the bar ahead (`Z   Q |C6`, `}   N2 |`), text after the final barline
  // (`Z<Blues in C>`) — belong to the bars either side, never to a bar of
  // their own, which would add a silent bar to the form.
  let outside = false;

  /** A fresh bar taking the marks written ahead of its first cell. */
  const carryForward = (from: PendingMeasure): PendingMeasure => {
    const next = blank();
    if (from._open) next._open = from._open;
    if (from.spacer) next.spacer = from.spacer;
    if (from.section != null) next.section = from.section;
    if (from.timeSig) next.timeSig = from.timeSig;
    if (from.ending != null) next.ending = from.ending;
    if (from.segno) next.segno = true;
    // A coda sign written after the previous bar's music is the point the
    // chart leaves for the coda — the start of this bar, which is where
    // flattenMeasures takes the jump (`r|   Q }` in Un Poco Loco: the whole
    // A plays before the D.S. pass goes to the coda).
    if (from.coda) next.coda = true;
    return next;
  };

  /** Hand the text and end-of-bar marks of cells with no music to `into`. */
  const takeSuffix = (into: IRealMeasure, from: PendingMeasure) => {
    if (from.directive != null) into.directive ??= from.directive;
    if (from.staffText != null) {
      into.staffText = into.staffText ? `${into.staffText} ${from.staffText}` : from.staffText;
      if (from.staffTextAbove) into.staffTextAbove = true;
    }
    if (from.fermata) into.fermata = true;
    if (from.endMarker) into.endMarker = true;
  };

  const push = () => {
    if (simile && cur.chords.length === 0 && cur.barRepeat == null) {
      const into = simile;
      simile = null;
      into.cells = (into.cells ?? 1) + (cellCursor - measureStartCell);
      if (cur.close) into.close = cur.close;
      takeSuffix(into, cur);
      cur = carryForward(cur);
      measureStartCell = cellCursor;
      return;
    }
    if (outside && measures.length > 0 && cur.chords.length === 0 && cur.barRepeat == null) {
      takeSuffix(measures[measures.length - 1], cur);
      cur = carryForward(cur);
      measureStartCell = cellCursor;
      return;
    }
    if (isReal(cur)) {
      const startCell = measureStartCell;
      const width = Math.max(1, cellCursor - startCell);
      const barBeats = running[0];
      const cells = cur._cells ?? [];
      const { _open, _cells, ...rest } = cur;
      void _cells;
      const measure: IRealMeasure = { id: uid('m'), ...rest, cell: startCell, cells: width };
      if (_open) measure.open = _open;
      if (measure.chords.length > 0) {
        if (cells.length === measure.chords.length) {
          // Beats from cell spans (accurate to how the chart was written).
          measure.chords.forEach((ch, k) => {
            const offset = cells[k] - startCell;
            const nextOffset = k + 1 < cells.length ? cells[k + 1] - startCell : width;
            const span = Math.max(1, nextOffset - offset);
            ch.beats = Math.max(1, Math.round((span * barBeats) / width));
          });
          // Reconcile rounding so a bar's chord beats sum to its meter (the last
          // chord absorbs the drift, e.g. 3 evenly-spaced chords in 4/4 → 1,1,2).
          // An excess the last chord cannot give back without vanishing comes
          // off the longest chord before it (the later on a tie, moving the
          // fewest onsets): I Feel Pretty's `F sEb/G,C/E,` is four cells in
          // 3/4, and F:2 Eb/G:1 C/E:1 put C/E on the next bar's downbeat.
          const last = measure.chords[measure.chords.length - 1];
          let sum = measure.chords.reduce((s, c) => s + c.beats, 0);
          if (sum < barBeats) last.beats += barBeats - sum;
          while (sum > barBeats) {
            const give =
              last.beats > 1
                ? last
                : measure.chords.reduce<IRealChord | null>(
                    (best, c) => (c.beats > 1 && c.beats >= (best?.beats ?? 0) ? c : best),
                    null,
                  );
            if (!give) break; // more chords than beats: no whole-beat split fits
            give.beats -= 1;
            sum -= 1;
          }
        } else {
          const splits = distributeBeats(measure.chords.length, barBeats);
          measure.chords.forEach((ch, k) => (ch.beats = splits[k] ?? 1));
        }
      }
      measures.push(measure);
      // The sign straddles the simile's middle barline: only a sign bar closed
      // by a plain `|` has a second bar still to come.
      simile =
        measure.barRepeat === 2 && measure.chords.length === 0 && measure.close === 'single'
          ? measure
          : null;
      cur = blank();
    }
    measureStartCell = cellCursor;
  };

  const closeBar = (close: BarlineClose) => {
    cur.close = close;
    push();
    outside = close !== 'single';
  };

  const openBar = (open: BarlineOpen) => {
    if (cur.chords.length > 0 || cur.barRepeat != null) push();
    cur._open = open;
    outside = false;
  };

  let i = 0;
  const n = music.length;
  while (i < n) {
    const rest = music.slice(i);

    // Time signature (T + numerator + denominator).
    const ts = /^T(\d)(\d)/.exec(rest);
    if (ts) {
      running = parseTimeSig(ts[1], ts[2]);
      cur.timeSig = running;
      i += 3;
      continue;
    }

    // Ending bracket (N + digit).
    const end = /^N(\d)/.exec(rest);
    if (end) {
      cur.ending = parseInt(end[1], 10);
      if (cur.ending <= 1) beforeEndings = { ref: lastRef, noChord: noChordInForce };
      else if (beforeEndings) {
        lastRef = beforeEndings.ref;
        noChordInForce = beforeEndings.noChord;
      }
      i += 2;
      continue;
    }

    // Section / rehearsal mark (* + letter).
    const sec = /^\*([A-Za-z])/.exec(rest);
    if (sec) {
      cur.section = sec[1];
      i += 2;
      continue;
    }

    // Staff text / directive. A leading *nn is a vertical offset (00 below …
    // 74 above the staff); keep it to place the text above vs below the bar.
    const text = /^<(.*?)>/.exec(rest);
    if (text) {
      const offset = /^\*(\d\d)/.exec(text[1]);
      const body = text[1].replace(/^\*\d\d/, '').trim();
      if (DIRECTIVE_RE.test(body)) cur.directive = body;
      else if (body) {
        cur.staffText = body;
        if (offset && parseInt(offset[1], 10) >= 50) cur.staffTextAbove = true;
      }
      i += text[0].length;
      continue;
    }

    // A chord (occupies one layout cell).
    const chord = parseChordToken(rest);
    if (chord) {
      (cur._cells ??= []).push(cellCursor);
      cellCursor += 1;
      cur.chords.push({
        id: uid('c'),
        ...chord.ref,
        alternate: chord.alternate,
        beats: 0,
        small: smallOn || undefined,
        symbol: prettyChordSymbol(chord.ref),
      });
      lastRef = chord.ref;
      noChordInForce = false;
      i += chord.length;
      continue;
    }

    // Invisible root `W`: a cell that inherits the previous chord's root (used
    // to place a bass note, i.e. a slash chord, without reprinting the root).
    const inv = /^W(\/[A-G][#b]?)?/.exec(rest);
    if (inv) {
      // Only consume a cell / emit a chord when there's a root to inherit; an
      // orphaned W (no prior chord) is malformed — skip it without disturbing
      // the cell grid so later measures keep their positions.
      if (lastRef) {
        (cur._cells ??= []).push(cellCursor);
        cellCursor += 1;
        const bass = inv[1] ? inv[1].slice(1) : lastRef.bass;
        const ref: IRealChordRef = { ...lastRef, ...(bass ? { bass } : {}) };
        cur.chords.push(heldChord(ref));
        lastRef = ref;
        noChordInForce = false;
      }
      i += inv[0].length;
      continue;
    }

    // A standalone alternate `(X)`: one written against the cell before it —
    // a blank cell (`C^7(C#-7)  (F#7) |`), a slash or a bar repeat (`x(C7b9)`)
    // — rather than attached to a chord, which parseChordToken consumes. It is
    // display-only: iReal draws it small above that cell and plays the harmony
    // in force, so it takes no cell of its own and never becomes a chord. It
    // is shown as the alternate of the chord in force when that chord has no
    // alternate of its own; a bar repeat has no chord to carry it.
    const alt = /^\(([^)]*)\)/.exec(rest);
    if (alt) {
      const inner = parseChordToken(alt[1]);
      const last = cur.chords[cur.chords.length - 1];
      if (inner && last && !last.noChord && !last.alternate) last.alternate = inner.ref;
      i += alt[0].length;
      continue;
    }

    // Single-character tokens.
    const ch = music[i];
    switch (ch) {
      case '|':
        closeBar('single');
        break;
      case '[':
        openBar('double');
        break;
      case ']':
        closeBar('double');
        break;
      case '{':
        openBar('repeat');
        break;
      case '}':
        closeBar('repeat');
        break;
      case 'Z':
        closeBar('final');
        break;
      case 'n':
        (cur._cells ??= []).push(cellCursor);
        cellCursor += 1;
        cur.chords.push(noChord());
        noChordInForce = true;
        break;
      case 'x':
        cur.barRepeat = 1;
        cellCursor += 1;
        break;
      case 'r':
        cur.barRepeat = 2;
        cellCursor += 1;
        break;
      case 's':
        smallOn = true;
        break;
      case 'l':
        smallOn = false;
        break;
      case 'S':
        cur.segno = true;
        break;
      case 'Q':
        cur.coda = true;
        break;
      case 'f':
        cur.fermata = true;
        break;
      case 'U':
        // iReal's END marker: the tune stops at the end of this bar on the
        // final pass. Cold Duck Time writes `Ebadd9 U<Fine> F7 | n | n }` —
        // a two-bar band break inside the repeat that every chorus plays
        // except the last, which ends at the Fine. Dropping the token made
        // the last time through play the break and loop like every other.
        cur.endMarker = true;
        break;
      case 'p':
        // A slash plays the chord in force for a cell. After a chord in the
        // bar it just lengthens that chord's span. Heading a bar (`|ppD-7 |`)
        // it holds the chord from before the barline, so the bar restates it
        // — a bar's chords lay out from its first beat, and D-7 would
        // otherwise sound on beat 1 and cut the held chord short.
        if (cur.chords.length === 0 && cur.barRepeat == null) {
          const held = noChordInForce ? noChord() : lastRef ? heldChord(lastRef) : null;
          if (held) {
            (cur._cells ??= []).push(cellCursor);
            cur.chords.push(held);
          }
        }
        cellCursor += 1;
        break;
      case ' ':
        // An empty cell.
        cellCursor += 1;
        break;
      case 'Y':
        // Vertical spacer (extra space above the staff). Preserved for a lossless
        // round-trip; it does not consume a horizontal layout cell.
        cur.spacer = (cur.spacer ?? 0) + 1;
        break;
      default:
        // comma (separator), unknown.
        break;
    }
    i += 1;
  }
  // Flush any trailing measure.
  push();

  if (measures.length > 0 && !measures[0].timeSig) measures[0].timeSig = initialTimeSig;
  return measures;
}

/** One entry in a playlist index: enough to browse, plus the verbatim chunk. */
export interface PlaylistEntry {
  title: string;
  composer: string;
  /** The trimmed `=`-delimited song chunk, for on-demand full parsing. */
  chunk: string;
}

/** A lightweight playlist index — title/composer per song, no music decoded. */
export interface IRealIndex {
  scheme: string;
  name?: string;
  songs: PlaylistEntry[];
}

/** Split a decoded payload into the song chunks + trailing playlist name. */
function splitSongs(decoded: string): { chunks: string[]; name?: string } {
  const parts = decoded.split('===');
  let name: string | undefined;
  if (parts.length > 1) name = parts.pop();
  const chunks = parts.map((p) => p.trim()).filter((p) => p.length > 0);
  return { chunks, name: name?.trim() || undefined };
}

/** Cheap header-only metadata from a song chunk — style and (prettified) key
 * without unscrambling the music. Feeds the tune browser's filters, which read
 * 1400+ rows at once; full parsing stays on-demand per tune. */
export function peekSongMeta(scheme: string, song: string): { style?: string; key?: string } {
  const fields = song.split('=');
  if (scheme === 'irealbook') {
    const style = (fields[2] ?? '').trim();
    const key = (fields[3] ?? '').trim();
    return { style: style || undefined, key: key ? normalizeKey(key) : undefined };
  }
  let musicIndex = fields.findIndex((f) => f.includes(MUSIC_PREFIX));
  if (musicIndex < 0) musicIndex = 6;
  const style = (fields[musicIndex - 3] ?? '').trim();
  const key = (fields[musicIndex - 2] ?? '').trim();
  return { style: style || undefined, key: key ? normalizeKey(key) : undefined };
}

function parseSongFields(scheme: string, song: string): IRealChart | null {
  const fields = song.split('=');
  if (fields.length < 5) return null;

  if (scheme === 'irealbook') {
    // Plaintext: Title=Composer=Style=Key=n=Music
    const music = fields[5] ?? '';
    if (!music.trim()) return null;
    const timeSignature: [number, number] = [4, 4];
    return {
      title: normalizeTitle(fields[0] ?? 'Untitled'),
      composer: normalizeComposer(fields[1] ?? ''),
      style: (fields[2] ?? '').trim(),
      key: normalizeKey(fields[3] ?? 'C'),
      timeSignature,
      measures: tokenizeMeasures(music, timeSignature),
    };
  }

  // New format: anchor on the music field (the one bearing the prefix).
  let musicIndex = fields.findIndex((f) => f.includes(MUSIC_PREFIX));
  if (musicIndex < 0) musicIndex = 6;
  const rawMusic = fields[musicIndex] ?? '';
  const prefixPos = rawMusic.indexOf(MUSIC_PREFIX);
  if (prefixPos < 0) return null;
  const music = unscramble(rawMusic.slice(prefixPos));

  const title = normalizeTitle(fields[0] ?? 'Untitled');
  const composer = normalizeComposer(fields[1] ?? '');
  const style = (fields[musicIndex - 3] ?? '').trim();
  const key = normalizeKey(fields[musicIndex - 2] ?? 'C');
  const bpm = parseInt(fields[musicIndex + 2] ?? '', 10);
  const repeats = parseInt(fields[musicIndex + 3] ?? '', 10);
  const timeSignature: [number, number] = [4, 4];

  return {
    title,
    composer,
    style,
    key,
    tempo: Number.isFinite(bpm) && bpm > 0 ? bpm : undefined,
    repeats: Number.isFinite(repeats) && repeats > 0 ? repeats : undefined,
    timeSignature,
    measures: tokenizeMeasures(music, timeSignature),
  };
}

/**
 * Build a lightweight index of a playlist: just the title and composer of each
 * song (cheap — no unscrambling or tokenizing), keeping the verbatim chunk so a
 * single song can be fully parsed on demand. Fast enough to scan the 1460-song
 * default jazz playlist. Unlike `parseIRealURL`, an empty list is not an error.
 */
export function parseIRealIndex(input: string): IRealIndex {
  const m = SCHEME_RE.exec(input.trim());
  if (!m) throw new Error('Not an iReal Pro link (expected irealb:// or irealbook://).');
  const scheme = m[1];
  const { chunks, name } = splitSongs(decode(m[2]));
  const songs: PlaylistEntry[] = [];
  for (const chunk of chunks) {
    const fields = chunk.split('=');
    if (fields.length < 5) continue; // same validity gate as parseSongFields
    songs.push({
      title: normalizeTitle(fields[0] ?? 'Untitled'),
      composer: normalizeComposer(fields[1] ?? ''),
      chunk,
    });
  }
  return { scheme, name, songs };
}

/** Fully parse a single song chunk (a `PlaylistEntry.chunk`) into a chart. */
export function parseIRealSongChunk(scheme: string, chunk: string): IRealChart | null {
  return parseSongFields(scheme, chunk);
}

/** Parse a full iReal Pro URL into a playlist (one or more charts). */
export function parseIRealURL(input: string): IRealPlaylist {
  const index = parseIRealIndex(input);
  const songs = index.songs
    .map((e) => parseIRealSongChunk(index.scheme, e.chunk))
    .filter((s): s is IRealChart => s !== null);
  if (songs.length === 0) throw new Error('No songs found in the iReal Pro link.');
  return { name: index.name, songs };
}

/** Parse the first song from an iReal Pro URL, or null on failure. */
export function parseIRealSong(input: string): IRealChart | null {
  try {
    return parseIRealURL(input).songs[0] ?? null;
  } catch {
    return null;
  }
}
